import type { Expression as BabelExpression, Identifier as BabelIdentifier } from "@babel/types";

//
// Block Statement.
//

export interface BlockStatement extends NodeBase {
	type: "BlockStatement";
	body: (Expression | Statement)[];
}

export const isBlockStatement = (
	o: BlockStatement | Expression | Statement | undefined
): o is BlockStatement => {
	return o?.type === "BlockStatement";
};

//
// Expressions.
//

export type Expression =
	| ArrayExpression
	| ArrayPattern
	| ArrowFunctionExpression
	| AssignmentExpression
	| AwaitExpression
	| BinaryExpression
	| CallExpression
	| ConditionalExpression
	| FunctionExpression
	| Identifier
	| ImportExpression
	| Literal
	| LogicalExpression
	| MemberExpression
	| NewExpression
	| ObjectExpression
	| ObjectPattern
	| RestElement
	| SpreadElement
	| TaggedTemplateExpression
	| TemplateLiteral
	| ThisExpression
	| UnaryExpression
	| UpdateExpression
	| YieldExpression;

export interface NodeBase {
	start: number;
	end: number;
}

export interface ArrayExpression extends NodeBase {
	type: "ArrayExpression";
	elements: (Expression | SpreadElement | null)[];
}

export const isArrayExpression = (
	o: Expression | BlockStatement | Statement
): o is ArrayExpression => {
	return o && o.type === "ArrayExpression";
};

export interface ArrayPattern extends NodeBase {
	type: "ArrayPattern";
	elements: Expression[];
}

export const isArrayPattern = (o: Expression | BlockStatement | Statement): o is ArrayPattern => {
	return o && o.type === "ArrayPattern";
};

export interface ArrowFunctionExpression extends NodeBase {
	type: "ArrowFunctionExpression";
	id: null;
	expression: true;
	generator: boolean;
	async: boolean;
	params: Identifier[];
	body: Expression | BlockStatement;
}

export const isArrowFunctionExpression = (
	o: Expression | BlockStatement | Statement
): o is ArrowFunctionExpression => {
	return o && o.type === "ArrowFunctionExpression";
};

export interface AssignmentExpression extends NodeBase {
	type: "AssignmentExpression";
	operator: "=";
	left: Expression;
	right: Expression;
}

export const isAssignmentExpression = (
	o: Expression | BlockStatement | Statement
): o is AssignmentExpression => {
	return o && o.type === "AssignmentExpression";
};

export interface AwaitExpression extends NodeBase {
	type: "AwaitExpression";
	argument: Expression;
}

export const isAwaitExpression = (
	o: Expression | BlockStatement | Statement
): o is AwaitExpression => {
	return o && o.type === "AwaitExpression";
};

export interface BinaryExpression extends NodeBase {
	type: "BinaryExpression";
	left: Expression;
	operator: string;
	right: Expression;
}

export const isBinaryExpression = (
	o: Expression | BlockStatement | Statement
): o is BinaryExpression => {
	return o && o.type === "BinaryExpression";
};

export interface CallExpression extends NodeBase {
	type: "CallExpression";
	callee: Expression;
	arguments: Expression[];
}

export const isCallExpression = (
	o: Expression | BlockStatement | Statement
): o is CallExpression => {
	return o && o.type === "CallExpression";
};

export interface ConditionalExpression extends NodeBase {
	type: "ConditionalExpression";
	test: Expression;
	consequent: Expression;
	alternate: Expression;
}

export const isConditionalExpression = (
	o: Expression | BlockStatement | Statement
): o is ConditionalExpression => {
	return o && o.type === "ConditionalExpression";
};

export interface FunctionExpression extends NodeBase {
	type: "FunctionExpression";
	id: Identifier | null;
	expression: false;
	generator: boolean;
	async: boolean;
	params: Identifier[];
	body: BlockStatement;
}

export const isFunctionExpression = (
	o: Expression | BlockStatement | Statement
): o is FunctionExpression => {
	return o && o.type === "FunctionExpression";
};

export interface Identifier extends NodeBase {
	type: "Identifier";
	name: string;
}

export const isIdentifier = (
	o: Expression | BlockStatement | Statement | Identifier | BabelIdentifier | ViewExpression
): o is Identifier => {
	return o && o.type === "Identifier" && isString(o.name);
};

export interface ImportExpression extends NodeBase {
	type: "ImportExpression";
	source: Expression;
}

export const isImportExpression = (
	o: Expression | BlockStatement | Statement
): o is ImportExpression => {
	return o && o.type === "ImportExpression";
};

export interface Literal extends NodeBase {
	type: "Literal";
	value: number | string;
	raw: string;
}

export const isLiteral = (o: Expression | BlockStatement | Statement): o is Literal => {
	return (
		o &&
		o.type === "Literal" &&
		(o.value === undefined ||
			o.value === null ||
			typeof o.value === "number" ||
			typeof o.value === "boolean" ||
			isString(o.value))
	);
};

export interface LogicalExpression extends NodeBase {
	type: "LogicalExpression";
	left: Expression;
	operator: string;
	right: Expression;
}

export const isLogicalExpression = (
	o: Expression | BlockStatement | Statement
): o is LogicalExpression => {
	return o && o.type === "LogicalExpression";
};

export interface MemberExpression extends NodeBase {
	type: "MemberExpression";
	object: Expression;
	property: Expression;
	computed: false;
}

export const isMemberExpression = (
	o: Expression | BlockStatement | Statement
): o is MemberExpression => {
	return o && o.type === "MemberExpression";
};

export interface NewExpression extends NodeBase {
	type: "NewExpression";
	callee: Expression;
	arguments: Expression[];
}

export const isNewExpression = (o: Expression | BlockStatement | Statement): o is NewExpression => {
	return o && o.type === "NewExpression";
};

export interface ObjectExpression extends NodeBase {
	type: "ObjectExpression";
	properties: Property[];
}

export const isObjectExpression = (
	o: Expression | BlockStatement | Statement
): o is ObjectExpression => {
	return o && o.type === "ObjectExpression";
};

export interface ObjectPattern extends NodeBase {
	type: "ObjectPattern";
	properties: Property[];
}

export const isObjectPattern = (o: Expression | BlockStatement | Statement): o is ObjectPattern => {
	return o && o.type === "ObjectPattern";
};

export interface RestElement extends NodeBase {
	type: "RestElement";
	argument: Expression;
}

export const isRestElement = (
	o: Expression | BlockStatement | Statement | Property
): o is RestElement => {
	return o && o.type === "RestElement";
};

export interface SpreadElement extends NodeBase {
	type: "SpreadElement";
	argument: Expression;
}

export const isSpreadElement = (
	o: Expression | BlockStatement | Statement | Property
): o is SpreadElement => {
	return o && o.type === "SpreadElement";
};

export interface TaggedTemplateExpression extends NodeBase {
	type: "TaggedTemplateExpression";
	tag: Identifier;
	quasi: TemplateLiteral;
}

export const isTaggedTemplateExpression = (
	o:
		| Expression
		| BlockStatement
		| Statement
		| ImportDeclaration
		| ViewExpression
		| BabelExpression
		| undefined
		| null
): o is TaggedTemplateExpression => {
	return (
		o?.type === "TaggedTemplateExpression" &&
		isIdentifier((o as TaggedTemplateExpression).tag) &&
		isTemplateLiteral((o as TaggedTemplateExpression).quasi)
	);
};

export interface TemplateLiteral extends NodeBase {
	type: "TemplateLiteral";
	expressions: Expression[];
	quasis: {
		type: "TemplateElement";
		value: { raw: string; cooked: string };
		tail: true;
	}[];
}

export const isTemplateLiteral = (
	o: Expression | BlockStatement | Statement
): o is TemplateLiteral => {
	return (
		o && o.type === "TemplateLiteral" && Array.isArray(o.expressions) && Array.isArray(o.quasis)
	);
};

export interface ThisExpression extends NodeBase {
	type: "ThisExpression";
}

export const isThisExpression = (
	o: Expression | BlockStatement | Statement
): o is ThisExpression => {
	return o && o.type === "ThisExpression";
};

export interface UnaryExpression extends NodeBase {
	type: "UnaryExpression";
	operator: string;
	prefix: boolean;
	argument: Expression;
}

export const isUnaryExpression = (
	o: Expression | BlockStatement | Statement
): o is UnaryExpression => {
	return o && o.type === "UnaryExpression";
};

export interface UpdateExpression extends NodeBase {
	type: "UpdateExpression";
	operator: string;
	prefix: boolean;
	argument: Expression;
}

export const isUpdateExpression = (
	o: Expression | BlockStatement | Statement
): o is UpdateExpression => {
	return o && o.type === "UpdateExpression";
};

export interface YieldExpression extends NodeBase {
	type: "YieldExpression";
	delegate: boolean;
	argument: Expression;
}

export const isYieldExpression = (
	o: Expression | BlockStatement | Statement | undefined
): o is YieldExpression => {
	return o?.type === "YieldExpression";
};

//
// Statements.
//

export type Statement =
	| BreakStatement
	| DoWhileStatement
	| EmptyStatement
	| ExpressionStatement
	| ForOfStatement
	| ForStatement
	| FunctionDeclaration
	| IfStatement
	| ImportDeclaration
	| ReturnStatement
	| SwitchStatement
	| ThrowStatement
	| VariableDeclaration
	| WhileStatement;

export interface BreakStatement extends NodeBase {
	type: "BreakStatement";
	label: Identifier | null;
}

export const isBreakStatement = (
	o: Expression | BlockStatement | Statement
): o is BreakStatement => {
	return o && o.type === "BreakStatement";
};

export interface DoWhileStatement extends NodeBase {
	type: "DoWhileStatement";
	test: Expression;
	body: Expression | BlockStatement;
}

export const isDoWhileStatement = (
	o: Expression | BlockStatement | Statement
): o is DoWhileStatement => {
	return o && o.type === "DoWhileStatement";
};

export interface EmptyStatement extends NodeBase {
	type: "EmptyStatement";
}

export const isEmptyStatement = (
	o: Expression | BlockStatement | Statement
): o is EmptyStatement => {
	return o && o.type === "EmptyStatement";
};

export interface ExpressionStatement extends NodeBase {
	type: "ExpressionStatement";
	expression: Expression;
}

export const isExpressionStatement = (
	o: Expression | BlockStatement | Statement | undefined
): o is ExpressionStatement => {
	return o?.type === "ExpressionStatement";
};

export interface ForOfStatement extends NodeBase {
	type: "ForOfStatement";
	await: boolean;
	left: VariableDeclaration;
	right: Expression;
	body: BlockStatement;
}

export const isForOfStatement = (
	o: Expression | BlockStatement | Statement | undefined
): o is ForOfStatement => {
	return o?.type === "ForOfStatement";
};

export interface ForStatement extends NodeBase {
	type: "ForStatement";
	init: VariableDeclaration | null;
	test: Expression | null;
	update: Expression | null;
	body: BlockStatement;
}

export const isForStatement = (
	o: Expression | BlockStatement | Statement | undefined
): o is ForStatement => {
	return o?.type === "ForStatement";
};

export interface FunctionDeclaration extends NodeBase {
	type: "FunctionDeclaration";
	id: Identifier;
	expression: boolean;
	generator: boolean;
	async: boolean;
	params: Identifier[];
	body: BlockStatement;
}

export const isFunctionDeclaration = (
	o: Expression | BlockStatement | Statement
): o is FunctionDeclaration => {
	return o && o.type === "FunctionDeclaration";
};

export interface IfStatement extends NodeBase {
	type: "IfStatement";
	test: Expression;
	consequent: Expression;
	alternate: Expression;
}

export const isIfStatement = (
	o: Expression | BlockStatement | Statement | undefined
): o is IfStatement => {
	return o?.type === "IfStatement";
};

export interface ImportDeclaration extends NodeBase {
	type: "ImportDeclaration";
	specifiers: ImportSpecifier[];
	source: Literal;
}

export const isImportDeclaration = (
	o: Expression | BlockStatement | Statement
): o is ImportDeclaration => {
	return o && o.type === "ImportDeclaration";
};

export interface ReturnStatement extends NodeBase {
	type: "ReturnStatement";
	argument: Expression;
}

export const isReturnStatement = (
	o: Expression | BlockStatement | Statement | undefined
): o is ReturnStatement => {
	return o?.type === "ReturnStatement";
};

export interface SwitchStatement extends NodeBase {
	type: "SwitchStatement";
	discriminant: Expression;
	cases: SwitchCase[];
}

export const isSwitchStatement = (
	o: Expression | BlockStatement | Statement
): o is SwitchStatement => {
	return o && o.type === "SwitchStatement";
};

export interface SwitchCase extends NodeBase {
	type: "SwitchCase";
	test: Expression | null;
	consequent: Statement[];
}

export interface ThrowStatement extends NodeBase {
	type: "ThrowStatement";
	argument: Expression;
}

export const isThrowStatement = (
	o: Expression | BlockStatement | Statement
): o is ThrowStatement => {
	return o && o.type === "ThrowStatement";
};

export interface VariableDeclaration extends NodeBase {
	type: "VariableDeclaration";
	declarations: VariableDeclarator[];
	kind: "const" | "let" | "var";
}

export const isVariableDeclaration = (
	o: Expression | BlockStatement | Statement | undefined
): o is VariableDeclaration => {
	return (
		o?.type === "VariableDeclaration" &&
		(o.kind === "const" || o.kind === "let" || o.kind === "var")
	);
};

export interface WhileStatement extends NodeBase {
	type: "WhileStatement";
	test: Expression;
	body: Expression | BlockStatement;
}

export const isWhileStatement = (
	o: Expression | BlockStatement | Statement | undefined
): o is WhileStatement => {
	return o?.type === "WhileStatement";
};

//
// Specialty types that can't or shouldn't be emitted directly, e.g., VariableDeclarator, which
// exists only as a childe of VariableDeclaration.
//

export interface Property extends NodeBase {
	type: "Property";
	method: boolean;
	shorthand: boolean;
	computed: boolean;
	key: Identifier | Literal;
	value: Expression;
	kind: string;
}

export interface ImportSpecifier extends NodeBase {
	type: "ImportSpecifier";
	view: false;
	mutable: false;
	imported: Identifier;
	local: Identifier;
}

export interface ViewExpression extends NodeBase {
	type: "ViewExpression";
	id: Identifier;
}

export const isViewExpression = (o: ViewExpression | Identifier): o is ViewExpression => {
	return o && o.type === "ViewExpression" && isIdentifier(o.id);
};

export interface VariableDeclarator extends NodeBase {
	type: "VariableDeclarator";
	id: Identifier;
	init: Expression | BlockStatement | null;
}

//
// Misc.
//

export const isString = (o: unknown): o is string => {
	return typeof o === "string" || o instanceof String;
};
